import { Inject, Injectable } from '@angular/core';
import { CommonFireStoreDao, QueryParam, WhereFilterOperandKeys } from 'ag-common-svc/lib/dao/CommonFireStoreDao.dao';
import { map, Observable, shareReplay } from 'rxjs';
import { FirebaseApp } from 'firebase/app';
import { FIREBASE_APP } from 'ag-common-svc/lib/injections/firebase-app';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import {
  ChangeSourceType,
  ConferenceLogBulkInviteeOutcomeStatus,
  ConferenceLogBulkInviteeOutcomeStatusKeys,
} from '@ag-common-lib/public-api';
import { CONFERENCES_COLLECTION_PATH } from 'ag-common-svc/lib/services/conference.service';
import { ToastrService } from 'ngx-toastr';

export const OUTCOME_STATUS_LOG_COLLECTION = 'log-bulk-invitee-outcome-status';

@Injectable({
  providedIn: 'root',
})
export class ConferenceBulkInviteeOutcomeStatusService {
  private readonly fsDao: CommonFireStoreDao<ConferenceLogBulkInviteeOutcomeStatus>;
  private readonly conferenceCollectionPath = CONFERENCES_COLLECTION_PATH;
  private readonly outcomeStatusLogCollectionPath = OUTCOME_STATUS_LOG_COLLECTION;
  private readonly outcomeStatusLogs$: Observable<ConferenceLogBulkInviteeOutcomeStatus[]>;

  constructor(
    @Inject(FIREBASE_APP) fireBaseApp: FirebaseApp,
    private toastrService: ToastrService,
  ) {
    this.fsDao = new CommonFireStoreDao<ConferenceLogBulkInviteeOutcomeStatus>(fireBaseApp, null, null);
    this.outcomeStatusLogs$ = this.getOutcomeStatusLogsCollection();
  }

  getOutcomeStatusLogs() {
    return this.outcomeStatusLogs$;
  }

  private getOutcomeStatusLogsCollection() {
    return this.fsDao.getCollectionGroupSnapshot(this.outcomeStatusLogCollectionPath, [], true).pipe(
      map(snapshot =>
        snapshot.docs
          ?.map(doc => (doc.exists() ? doc.data() : null))
          .filter(Boolean)
          .sort((left, right) => {
            const leftDate = left?.[BaseModelKeys.updatedDate]?.getTime?.() ?? 0;
            const rightDate = right?.[BaseModelKeys.updatedDate]?.getTime?.() ?? 0;

            return rightDate - leftDate;
          }),
      ),
      shareReplay(1),
    );
  }

  getConferenceOutcomeStatusLogsByConferenceId(
    conferenceDbId: string,
  ): Observable<ConferenceLogBulkInviteeOutcomeStatus[]> {
    let qp: QueryParam[] = [];
    qp.push(
      new QueryParam(
        ConferenceLogBulkInviteeOutcomeStatusKeys.conferenceDbId,
        WhereFilterOperandKeys.equal,
        conferenceDbId,
      ),
    );

    return this.fsDao.getCollectionGroupSnapshot(this.outcomeStatusLogCollectionPath, qp, true).pipe(
      map(snapshot => {
        return snapshot.docs.map(doc => {
          if (!doc.exists()) {
            return null;
          }
          const data = doc.data();

          return data;
        });
      }),
      shareReplay(1),
    );
  }

  async create(
    data: ConferenceLogBulkInviteeOutcomeStatus,
    silent = false,
    action: ChangeSourceType | null = ChangeSourceType.regularArm,
  ) {
    const conferenceDbId = data[ConferenceLogBulkInviteeOutcomeStatusKeys.conferenceDbId];
    const table = this.getCollectionPath(conferenceDbId);

    Object.assign(data, {
      [BaseModelKeys.actionName]: action,
    });

    const logs = await this.fsDao
      .create(data, table)
      .then(items => {})
      .catch(e => {
        console.log('e', e);
      });

    !silent && this.toastrService.success('Conference Logs Successfully Created!');

    return logs;
  }

  private getCollectionPath(conferenceId: string) {
    return [this.conferenceCollectionPath, conferenceId, this.outcomeStatusLogCollectionPath].join('/');
  }
}
